import icon from '@assets/fav.png';
import { PAGE_TITLES, ROUTE_PATHS } from '@constants/routes';
import { UI_SIZE } from '@constants/ui';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import type { SxProps, Theme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Stack from '@mui/material/Stack';
import { tabsClasses } from '@mui/material/Tabs';
import MuiToolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useLocation } from 'react-router-dom';
import MenuButton from '../Menu/MenuButton';
import CompactSideBar from '../Sidebar/CompactSideBar';

/** Only rendered below `lg`, where the permanent sidebar is hidden. */
const AppNavbar: React.FC<{ toggleDrawer: () => void, open: boolean }> = ({ toggleDrawer, open }) => {
  const location = useLocation();

  return (
    <AppBar position='fixed' sx={appBarStyles}>
      <MuiToolbar variant='regular' sx={toolbarStyles}>
        <Stack direction='row' sx={stackStyles}>
          <Stack direction='row' spacing={1} sx={{ justifyContent: 'center', marginRight: 'auto' }}>
            <img src={icon} width={36} height={36} alt='' />
            <Typography variant='h4' component='h1' sx={{ color: 'text.primary', paddingLeft: '12px' }}>
              {generatePathName(location.pathname)}
            </Typography>
          </Stack>
          <MenuButton aria-label='menu' onClick={toggleDrawer}>
            <MenuRoundedIcon />
          </MenuButton>
          <CompactSideBar open={open} toggleDrawer={toggleDrawer} />
        </Stack>
      </MuiToolbar>
    </AppBar>
  );
};

const appBarStyles: SxProps<Theme> = {
  height: UI_SIZE.APP_NAVBAR_HEIGHT,
  display: { xs: 'auto', md: 'auto', lg: 'none' },
  boxShadow: 0,
  bgcolor: 'background.paper',
  backgroundImage: 'none',
  borderBottom: '1px solid',
  borderColor: 'divider',
  top: 'var(--template-frame-height, 0px)',
};

const toolbarStyles: SxProps<Theme> = {
  width: '100%',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  justifyContent: 'center',
  gap: '12px',
  flexShrink: 0,
  [`& .${tabsClasses.list}`]: { gap: '8px', p: '8px', pb: 0 },
};

const stackStyles: SxProps<Theme> = {
  alignItems: 'center',
  flexGrow: 1,
  width: '100%',
  gap: 1,
};

/**
 * The page's name as the menu writes it, never its path: the Enrollment page lives at `/activation`,
 * and a title made from the path read "Activation" there and "Seat Sessions" beside the menu's
 * "Seat sessions". A page under another one, such as a license's seat usage, takes its parent's.
 */
const generatePathName = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return PAGE_TITLES[ROUTE_PATHS.ACTIVATION];

  return (PAGE_TITLES as Record<string, string>)[`/${segments[0]}`] ?? segments[0].split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
};

export default AppNavbar;
